import { useQuery } from '@tanstack/react-query';
import type { ValidationIssue, WorkflowDefinition } from '@troia-v3/types';

import { useDebouncedValue } from '@/shared/hooks';

import { workflowsApi } from '../services/workflowsApi';

/**
 * Confere o desenho no servidor enquanto o dono edita (2026-08-27).
 *
 * O servidor é a ÚNICA fonte de verdade das regras estruturais — espelhá-las
 * aqui significaria manter duas implementações e fazer toda regra nova nascer
 * com dívida de front.
 *
 * Três cuidados que o código abaixo materializa:
 *
 * 1. A chave de conferência sai só de `nodes` + `edges`. O `viewport` fica de
 *    fora de propósito: ele muda a cada arrasto/zoom do canvas e, dentro da
 *    chave, cada arrasto viraria uma requisição.
 * 2. O atraso usa o `useDebouncedValue` que já existe no projeto. Ele solta o
 *    valor inicial SEM atraso, então a primeira conferência sai na montagem —
 *    que é o desejado (abrir um workflow já quebrado mostra o erro na hora).
 * 3. Desenho vazio (nenhum nó) não é conferido: o Zod do backend exige pelo
 *    menos um nó e responderia 422 — ruído, não informação.
 *
 * A raiz da queryKey (`workflows-validate`) é propositalmente diferente de
 * `workflowKeys.all` (`['workflows']`): assim o `invalidateQueries` das
 * mutações de CRUD não dispara uma rodada extra de conferência.
 *
 * `conferido` e `conferenciaFalhou` existem porque "lista de problemas vazia"
 * NÃO é sinônimo de "está tudo certo": com o servidor fora do ar (ou num 422 de
 * esquema) a lista também vem vazia, e o aviso está calado de propósito
 * (`_handleErrorLocally`). Sem esses dois, o painel daria atestado de saúde a um
 * desenho que ninguém conferiu. Quem desenha o painel só pode afirmar "sem erro"
 * quando `conferido` for verdadeiro.
 */
export function useValidateWorkflow(definition: WorkflowDefinition | null): {
  issues: ValidationIssue[];
  isValidating: boolean;
  /** Veio resposta boa do servidor para o desenho atual. */
  conferido: boolean;
  /** A conferência falhou (servidor fora, rede caída, 422 de esquema). */
  conferenciaFalhou: boolean;
} {
  const chave =
    definition === null || definition.nodes.length === 0
      ? ''
      : JSON.stringify({
          nodes: definition.nodes,
          edges: definition.edges,
          variables: {},
        });
  const chaveAtrasada = useDebouncedValue(chave, 600);

  const { data, isFetching, isSuccess, isError } = useQuery({
    queryKey: ['workflows-validate', chaveAtrasada],
    queryFn: () => workflowsApi.validateDefinition(JSON.parse(chaveAtrasada) as WorkflowDefinition),
    enabled: chaveAtrasada !== '',
    staleTime: Infinity,
    retry: false,
  });

  return {
    issues: data?.issues ?? [],
    isValidating: isFetching,
    conferido: isSuccess,
    conferenciaFalhou: isError,
  };
}
